import React, { useState, useEffect, useContext } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Form from '@amzn/awsui-components-console/form';
import FormField from '@amzn/awsui-components-console/form-field';
import Header from '@amzn/awsui-components-console/header';
import Input from '@amzn/awsui-components-console/input';
import Select from '@amzn/awsui-components-console/select';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { RegionContext } from '../CustomContext';
import { useDrawer } from '../DrawerContext';
import { createOverride } from './apis';
import { listAllBots } from '../api-auth/bot-accounts/apis';

export default function CreateOverridePanel({ deviceTypeOptions, prefillBotAccount }) {
    const [selectedBot, setSelectedBot] = useState(null);
    const [botOptions, setBotOptions] = useState([]);
    const [selectedDeviceType, setSelectedDeviceType] = useState(null);
    const [allocation, setAllocation] = useState('');
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');

    const { selectedRegion } = useContext(RegionContext);
    const { closeDrawer, getCallbacks } = useDrawer();

    useEffect(() => {
        listAllBots()
            .then((resp) => {
                const bots = resp.response || resp || [];
                const options = bots.map((b) => ({
                    label: b.username || b.botAccount || b,
                    value: b.username || b.botAccount || b,
                }));
                setBotOptions(options);
                if (prefillBotAccount) {
                    const match = options.find((o) => o.value === prefillBotAccount);
                    if (match) setSelectedBot(match);
                }
            })
            .catch(() => setBotOptions([]));
    }, []);

    useEffect(() => {
        if (prefillBotAccount && botOptions.length > 0) {
            const match = botOptions.find((o) => o.value === prefillBotAccount);
            if (match) setSelectedBot(match);
        }
    }, [prefillBotAccount, botOptions]);

    const resetForm = () => {
        setSelectedBot(null);
        setSelectedDeviceType(null);
        setAllocation('');
        setReason('');
        setFormError('');
    };

    const handleSubmit = async () => {
        setLoading(true);
        setFormError('');
        try {
            await createOverride(
                selectedBot.value,
                selectedDeviceType.value,
                parseInt(allocation, 10),
                reason.trim(),
                selectedRegion.value
            );
            resetForm();
            const { onSuccess } = getCallbacks();
            if (onSuccess) {
                onSuccess();
            }
            closeDrawer();
        } catch (err) {
            if (err.response && err.response.data?.error_message) {
                setFormError(err.response.data.error_message);
            } else {
                setFormError('An error occurred while creating the override. Try again or contact support.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        resetForm();
        closeDrawer();
    };

    const isValid = selectedBot && selectedDeviceType && allocation && parseInt(allocation, 10) > 0;

    return (
        <Box padding="l">
            <Form
                header={<Header variant="h2">Add/Edit allocation override</Header>}
                errorText={formError}
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={!isValid || loading}
                            loading={loading}
                        >
                            Create
                        </Button>
                    </SpaceBetween>
                }
            >
                <SpaceBetween size="l">
                    <FormField label="Bot account" description="The bot account to override allocation for.">
                        <Select
                            selectedOption={selectedBot}
                            onChange={({ detail }) => setSelectedBot(detail.selectedOption)}
                            options={botOptions}
                            placeholder="Select a bot account"
                            filteringType="auto"
                        />
                    </FormField>
                    <FormField label="Device type" description="The device type this override applies to.">
                        <Select
                            selectedOption={selectedDeviceType}
                            onChange={({ detail }) => setSelectedDeviceType(detail.selectedOption)}
                            options={deviceTypeOptions}
                            placeholder="Select a device type"
                        />
                    </FormField>
                    <FormField
                        label="Allocation"
                        description="Maximum number of devices of this type the bot can hold simultaneously."
                        constraintText="Must be a positive number."
                    >
                        <Input
                            value={allocation}
                            onChange={({ detail }) => setAllocation(detail.value)}
                            type="number"
                            placeholder="5"
                        />
                    </FormField>
                    <FormField label="Reason" description="Business justification for this override.">
                        <Input
                            value={reason}
                            onChange={({ detail }) => setReason(detail.value)}
                            placeholder="Team needs higher capacity for CI/CD"
                        />
                    </FormField>
                </SpaceBetween>
            </Form>
        </Box>
    );
}
